import React, { Suspense, useEffect } from 'react'
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { CSpinner } from '@coreui/react'
import { getCurrentUser } from './store/authSlice'
import './App.css'

// Layouts
const DefaultLayout = React.lazy(() => import('./layout/DefaultLayout'))

// Pages
const Login = React.lazy(() => import('./views/pages/Login'))
const Page404 = React.lazy(() => import('./views/pages/Page404'))

const loading = (
  <div className="pt-3 text-center">
    <CSpinner color="primary" variant="grow" />
  </div>
)

// Protected Route Component
const ProtectedRoute = ({ children, isInitializing }) => {
  const { isAuthenticated, token } = useSelector((state) => state.auth)
  
  // Show loading while initializing
  if (isInitializing) {
    return loading
  }
  
  // Check if authenticated OR if token exists in localStorage
  const hasToken = token || localStorage.getItem('token')
  
  if (!isAuthenticated && !hasToken) {
    return <Navigate to="/login" replace />
  }
  
  return children
}

const App = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const [isInitializing, setIsInitializing] = React.useState(true)
  const { isAuthenticated, user } = useSelector((state) => state.auth)

  useEffect(() => {
    // Initialize auth state from localStorage if not already authenticated
    const storedToken = localStorage.getItem('token')
    
    if (storedToken && !isAuthenticated) {
      // Verify token with backend (.unwrap() throws on rejection so .catch() actually fires)
      dispatch(getCurrentUser())
        .unwrap()
        .then((userData) => {
          // Check if user is account-admin and should be redirected
          const user = userData?.data || userData?.user || userData
          if (user?.roles?.some((role) => role.name === 'account-admin')) {
            navigate('/account-settings', { replace: true })
          }
        })
        .catch(() => {
          // forceLogout() in the interceptor already cleared localStorage and redirects.
          // Nothing extra needed here.
        })
        .finally(() => {
          setIsInitializing(false)
        })
    } else {
      setIsInitializing(false)
    }
  }, [dispatch, isAuthenticated, navigate])

  // Also check after user state changes (e.g., after login)
  useEffect(() => {
    if (isAuthenticated && user && !isInitializing) {
      // Check if user is account-admin and redirect them away from super-admin-only paths
      if (user?.roles?.some((role) => role.name === 'account-admin')) {
        const accountAdminPaths = ['/dashboard', '/account-settings', '/sites', '/meters', '/zones', '/analytics']
        const isOnValidPath =
          accountAdminPaths.some((p) => location.pathname === p || location.pathname.startsWith(p + '/')) ||
          location.pathname.startsWith('/accounts/')
        if (!isOnValidPath) {
          navigate('/account-settings', { replace: true })
        }
      }
    }
  }, [isAuthenticated, user, isInitializing, navigate, location.pathname])

  return (
    <Suspense fallback={loading}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/404" element={<Page404 />} />
        <Route
          path="/*"
          element={
            <ProtectedRoute isInitializing={isInitializing}>
              <DefaultLayout />
            </ProtectedRoute>
          }
        />
      </Routes>
    </Suspense>
  )
}

export default App
